// The dispatch-plan page's order selector: the orders a dispatcher has in mind
// that the optimizer run didn't plan. Synced orders come from the order sync
// (search, or a pasted list) with their derived pallets; a manual order is one
// the sync doesn't have, typed in with its ZIP and address. Each picked order
// goes on the route the dispatcher chooses, with a delivery window and its
// pallets in the client's units (cm, kg). Nothing is optimized again: the
// page saves the picks with its edits and recalibrates.

import { useEffect, useState } from "react";

import {
  type AddedOrderPayload,
  getOrderPallets,
  listOrders,
  resolveOrders,
} from "../api/client";
import type { useT } from "../lib/i18n";
import {
  type CmPallet,
  STD_PALLET_CM,
  TIME_SLOTS,
  palletToCm,
  palletToFt,
} from "../lib/orderUnits";
import type { OrderSummary, PlanRoute } from "../types";
import { Dialog } from "./Dialog";

type T = ReturnType<typeof useT>;

// The "route" of an order put in the unrouted orders: its payload names none.
const UNROUTED = "unrouted";

interface PickedOrder {
  order_no: string;
  customer: string | null;
  manual: boolean;
  dest_zip: string | null;
  address: string;
  notes: string;
  route_id: string; // or UNROUTED
  after: string; // "HH:MM" or ""
  due: string;
  pallets: CmPallet[] | null; // null while the sync's are loading
  // Only pallets the dispatcher changed (or typed, for a manual order) are
  // sent; otherwise the server derives them exactly as the picker does.
  edited: boolean;
}

export function AddOrdersDialog({
  t,
  region,
  routes,
  routeLabel,
  initialRouteId,
  onPlan,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  t: T;
  region: string;
  routes: PlanRoute[]; // the routes an order may go on
  routeLabel: (routeId: string) => string;
  initialRouteId: string | null;
  onPlan: Map<string, string>; // order number → where it is on the plan
  busy: boolean;
  error: string | null;
  onConfirm: (added: AddedOrderPayload[]) => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState<OrderSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paste, setPaste] = useState("");
  const [pasteNote, setPasteNote] = useState<string[]>([]);
  const [picks, setPicks] = useState<PickedOrder[]>([]);
  // The plan's warehouse by default; the dispatcher may look at every one.
  const [allRegions, setAllRegions] = useState(false);
  // The order just picked: its route is focused next, as its "+" is gone.
  const [justPicked, setJustPicked] = useState<string | null>(null);
  const [manual, setManual] = useState({ order_no: "", customer: "", zip: "", address: "", notes: "" });
  const defaultRoute = initialRouteId ?? routes[0]?.route_id ?? UNROUTED;

  async function load(query: string, everywhere = allRegions) {
    setLoadError(null);
    try {
      setOrders(
        await listOrders({
          days: 14,
          region: everywhere ? undefined : region,
          search: query || undefined,
        }),
      );
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setOrders([]);
    }
  }

  useEffect(() => {
    if (justPicked) document.getElementById(`add-route-${justPicked}`)?.focus();
  }, [justPicked]);

  // Loaded once on open; searching reloads.
  useEffect(() => {
    void load("");
  }, []);

  const picked = new Set(picks.map((p) => p.order_no.toUpperCase()));

  function patch(orderNo: string, change: Partial<PickedOrder>) {
    setPicks((all) => all.map((p) => (p.order_no === orderNo ? { ...p, ...change } : p)));
  }

  function pickSynced(o: OrderSummary) {
    if (picked.has(o.order_no.toUpperCase()) || onPlan.has(o.order_no)) return;
    setPicks((all) => [
      ...all,
      {
        order_no: o.order_no,
        customer: o.customer_name,
        manual: false,
        dest_zip: o.dest_zip,
        address: "",
        notes: "",
        route_id: defaultRoute,
        after: "",
        due: "",
        pallets: null,
        edited: false,
      },
    ]);
    setJustPicked(o.order_no);
    getOrderPallets(o.order_no)
      .then((r) =>
        patch(o.order_no, { pallets: r.pallets.map(palletToCm) }),
      )
      // The sync derives them anyway: an editor isn't needed to add the order.
      .catch(() => patch(o.order_no, { pallets: [] }));
  }

  async function find() {
    const tokens = paste.split(/[\s,;]+/).filter(Boolean);
    if (tokens.length === 0) return;
    try {
      const { found, missing } = await resolveOrders(tokens);
      const already = found
        .filter((o) => onPlan.has(o.order_no))
        .map((o) => `${o.order_no} (${onPlan.get(o.order_no)})`);
      found.forEach(pickSynced);
      setPasteNote([
        ...(missing.length ? [t("add.notFound", { list: missing.join(", ") })] : []),
        ...(already.length ? [t("add.alreadyOn", { list: already.join(", ") })] : []),
      ]);
      setPaste("");
    } catch (e) {
      setPasteNote([e instanceof Error ? e.message : String(e)]);
    }
  }

  function pickManual() {
    const orderNo = manual.order_no.trim();
    if (!orderNo || picked.has(orderNo.toUpperCase())) return;
    setPicks((all) => [
      ...all,
      {
        order_no: orderNo,
        customer: manual.customer.trim() || null,
        manual: true,
        dest_zip: manual.zip.trim(),
        address: manual.address.trim(),
        notes: manual.notes.trim(),
        route_id: defaultRoute,
        after: "",
        due: "",
        pallets: [{ ...STD_PALLET_CM }],
        edited: true,
      },
    ]);
    setJustPicked(orderNo);
    setManual({ order_no: "", customer: "", zip: "", address: "", notes: "" });
  }

  function editPallet(orderNo: string, i: number, field: keyof CmPallet, value: number) {
    const pick = picks.find((p) => p.order_no === orderNo);
    if (!pick?.pallets) return;
    const pallets = pick.pallets.map((p, k) => (k === i ? { ...p, [field]: value } : p));
    patch(orderNo, { pallets, edited: true });
  }

  function payload(p: PickedOrder): AddedOrderPayload {
    const out: AddedOrderPayload = { order_no: p.order_no };
    if (p.route_id !== UNROUTED) out.route_id = p.route_id;
    if (p.after) out.arrival_after_clock = p.after;
    if (p.due) out.deadline_clock = p.due;
    if (p.manual) {
      out.manual = true;
      out.dest_zip = p.dest_zip ?? "";
      if (p.customer) out.customer_name = p.customer;
      if (p.address) out.address = p.address;
      if (p.notes) out.delivery_notes = p.notes;
    }
    if (p.edited && p.pallets) out.pallets = p.pallets.map(palletToFt);
    return out;
  }

  const ready =
    picks.length > 0 && picks.every((p) => p.route_id && p.pallets !== null) && !busy;
  const manualReady = manual.order_no.trim() !== "" && /^\d{5}$/.test(manual.zip.trim());

  return (
    <Dialog title={t("add.title")} busy={busy} onCancel={onCancel} wide>
      <p id="plan-dialog-desc" className="plan-muted">
        {t("add.desc")}
      </p>

      <div className="plan-add-grid">
        <section className="plan-add-source" aria-label={t("add.fromSync")}>
          <form
            className="plan-add-search"
            onSubmit={(e) => {
              e.preventDefault();
              void load(search.trim());
            }}
          >
            <input
              type="search"
              aria-label={t("add.search")}
              placeholder={t("add.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              {t("add.searchBtn")}
            </button>
            <label className="plan-add-everywhere">
              <input
                type="checkbox"
                checked={allRegions}
                onChange={(e) => {
                  setAllRegions(e.target.checked);
                  void load(search.trim(), e.target.checked);
                }}
              />{" "}
              {t("add.allRegions")}
            </label>
          </form>
          {loadError && <p className="plan-muted">{t("add.loadFailed", { message: loadError })}</p>}
          {orders === null ? (
            <p className="plan-muted">{t("add.loading")}</p>
          ) : orders.length === 0 ? (
            <p className="plan-muted">{t("add.none")}</p>
          ) : (
            <div className="plan-add-results">
              <table className="manifest">
                <thead>
                  <tr>
                    <th>{t("plan.col.order")}</th>
                    <th>{t("plan.col.customer")}</th>
                    <th>{t("plan.col.zip")}</th>
                    <th>{t("plan.col.pallets")}</th>
                    <th>{t("plan.col.weight")}</th>
                    <th>
                      <span className="sr-only">{t("plan.col.actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} aria-label={`${o.order_no} ${o.customer_name ?? ""}`.trim()}>
                      <td>{o.order_no}</td>
                      <td>{o.customer_name ?? "—"}</td>
                      <td>{o.dest_zip ?? "—"}</td>
                      <td>{o.received_pallets ?? "—"}</td>
                      <td>{o.weight_kg === null ? "—" : `${Math.round(o.weight_kg)} kg`}</td>
                      <td>
                        {onPlan.has(o.order_no) ? (
                          <span className="plan-muted">
                            {t("add.onPlan", { where: onPlan.get(o.order_no)! })}
                          </span>
                        ) : picked.has(o.order_no.toUpperCase()) ? (
                          <span className="plan-muted">{t("add.picked")}</span>
                        ) : (
                          <button
                            className="btn btn-ghost plan-icon-btn"
                            aria-label={t("add.add", { order: o.order_no })}
                            onClick={() => pickSynced(o)}
                          >
                            +
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="plan-add-paste">
            <label className="control">
              <span className="control-label">{t("add.paste")}</span>
              <textarea rows={2} value={paste} onChange={(e) => setPaste(e.target.value)} />
            </label>
            <button className="btn btn-secondary" onClick={() => void find()}>
              {t("add.find")}
            </button>
            {pasteNote.map((n) => (
              <p key={n} className="plan-muted">
                {n}
              </p>
            ))}
          </div>

          <fieldset className="plan-add-manual">
            <legend>{t("add.manualLegend")}</legend>
            {(
              [
                ["order_no", "add.orderNo"],
                ["customer", "add.customer"],
                ["zip", "add.zip"],
                ["address", "add.address"],
                ["notes", "add.notes"],
              ] as const
            ).map(([field, label]) => (
              <label key={field} className="control">
                <span className="control-label">{t(label)}</span>
                <input
                  value={manual[field]}
                  inputMode={field === "zip" ? "numeric" : undefined}
                  onChange={(e) => setManual({ ...manual, [field]: e.target.value })}
                />
              </label>
            ))}
            <button className="btn btn-secondary" disabled={!manualReady} onClick={pickManual}>
              {t("add.addManual")}
            </button>
          </fieldset>
        </section>

        <section className="plan-add-picks" aria-label={t("add.toAdd")}>
          <h4>{t("add.toAdd")}</h4>
          {picks.length === 0 && <p className="plan-muted">{t("add.nothingPicked")}</p>}
          {picks.map((p) => (
            <fieldset key={p.order_no} className="plan-add-pick">
              <legend>
                {p.order_no}
                {p.customer ? ` ${p.customer}` : ""}
              </legend>
              <div className="plan-add-pick-head">
                {p.manual && <span className="plan-tag">{t("plan.badgeManual")}</span>}
                {p.dest_zip && (
                  <span className="plan-muted">{t("plan.zipOf", { zip: p.dest_zip })}</span>
                )}
                <button
                  className="plan-link-btn"
                  onClick={() => setPicks((all) => all.filter((x) => x.order_no !== p.order_no))}
                >
                  {t("add.remove", { order: p.order_no })}
                </button>
              </div>
              <div className="plan-add-pick-fields">
                <label className="control">
                  <span className="control-label">{t("add.route")}</span>
                  <select
                    id={`add-route-${p.order_no}`}
                    value={p.route_id}
                    onChange={(e) => patch(p.order_no, { route_id: e.target.value })}
                  >
                    {routes.map((r) => (
                      <option key={r.route_id} value={r.route_id}>
                        {routeLabel(r.route_id)}
                      </option>
                    ))}
                    <option value={UNROUTED}>{t("add.unrouted")}</option>
                  </select>
                </label>
                <label className="control">
                  <span className="control-label">{t("add.notBefore")}</span>
                  <TimeSelect t={t} value={p.after} onChange={(v) => patch(p.order_no, { after: v })} />
                </label>
                <label className="control">
                  <span className="control-label">{t("add.dueBy")}</span>
                  <TimeSelect t={t} value={p.due} onChange={(v) => patch(p.order_no, { due: v })} />
                </label>
              </div>
              {p.pallets === null ? (
                <p className="plan-muted">{t("add.loadingPallets")}</p>
              ) : (
                <div className="plan-add-pallets">
                  <p className="plan-muted">{t("add.palletUnits")}</p>
                  {p.pallets.map((x, i) => (
                    <div key={i} className="plan-add-pallet">
                      <span className="plan-muted">#{i + 1}</span>
                      {(["length_cm", "width_cm", "height_cm", "weight_kg"] as const).map((f) => (
                        <input
                          key={f}
                          type="number"
                          min="0"
                          aria-label={t(`add.${f}`, { n: i + 1 })}
                          value={x[f]}
                          onChange={(e) => editPallet(p.order_no, i, f, Number(e.target.value))}
                        />
                      ))}
                      <button
                        className="btn btn-ghost plan-icon-btn"
                        aria-label={t("add.removePallet", { n: i + 1 })}
                        disabled={p.pallets!.length === 1}
                        onClick={() =>
                          patch(p.order_no, {
                            pallets: p.pallets!.filter((_, k) => k !== i),
                            edited: true,
                          })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="plan-link-btn"
                    onClick={() =>
                      patch(p.order_no, {
                        pallets: [...p.pallets!, { ...STD_PALLET_CM }],
                        edited: true,
                      })
                    }
                  >
                    {t("add.addPallet")}
                  </button>
                </div>
              )}
            </fieldset>
          ))}
        </section>
      </div>

      {error && (
        <div className="banner warn" role="alert">
          {error}
        </div>
      )}
      <div className="plan-dialog-actions">
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          {t("plan.cancel")}
        </button>
        <button
          className="btn btn-primary"
          disabled={!ready}
          onClick={() => onConfirm(picks.map(payload))}
        >
          {busy ? t("add.adding") : t("add.confirm", { n: picks.length })}
        </button>
      </div>
    </Dialog>
  );
}

function TimeSelect({
  t,
  value,
  onChange,
}: {
  t: T;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t("add.anyTime")}</option>
      {TIME_SLOTS.map((slot) => (
        <option key={slot} value={slot}>
          {slot}
        </option>
      ))}
    </select>
  );
}
